import { BlurFilter, Container, Texture, Sprite, Text } from 'pixi.js';
import { gsap } from 'gsap';
import { SmoothGraphics as Graphics } from '@pixi/graphics-smooth';
import { pixiComponents } from './index';
import { ChartBlockConfigs, gameImages, chartBlockStyles } from '../../fastTradeCore/constants';
import { isMobile } from '../../../../core/helpers/isMobile';
import { BetBlocks } from './betBlocks';

const { gameBg, pointerBg } = gameImages;
const {
  pointerX,
  pointerY,
  pointerPivotX,
  pointerPivotY,
  pointerStylesMob,
  pointerStylesWeb,
  chartLinePointerStylesWeb,
  chartLinePointerStylesMob,
} = chartBlockStyles;
const {
  bg1Y,
  bg2Y,
  bgScale1,
  bgScale2,
  mobHeight,
  webHeight,
  dotGrowth,
  bgAnchor1X,
  bgAnchor2X,
  parentContainerX,
  parentContainerY,
  deleteUnnecessaryDotsCount,
} = ChartBlockConfigs;

export class Chart {
  container = new Container();

  graphicsPoints = [0, 200, 100, 100, 200, 300, 250, 200, 350, 250, 400, 300];

  dots = [];

  pointerDot = 0;

  pointerDotDistance = 0;

  centerDot = 0;

  dotsMargin = isMobile() ? 14 : 24.28;

  dotGrowth = dotGrowth;

  scalePoints = [];

  constructor(parentContainer) {
    this.parentContainer = parentContainer;
    this.parentContainer.position = { x: parentContainerX, y: parentContainerY };
    this.parentContainer.addChild(this.container);
    this.addBackground();
    this.betBlocks = new BetBlocks(this.container);
    this.createTextureChartFill();
    this.addChartStrokeGraphics();
    this.addChartFillGraphics();
    this.createChartPointerLineGraphics();
    this.addChartPointer();
    this.betBlocks.handleBetActionsCreator(this.container);
    // setInterval(() => this.parseGraphicsPointsFromDots(), 1000 / 30);
    // setTimeout(async () => {
    //   for (let i = 0; i < 20; i += 1) {
    //     await this.addDot(this.pointerDot - (Math.random() - Math.random()) * 50);
    //   }
    // }, 3500);
  }

  async setDots(data) {
    this.dots = data;
    this.pointerDot = this.dots[this.dots.length - 1];
    this.centerDot = this.dots[this.dots.length - 1];
    this.parseGraphicsPointsFromDots();
  }

  async setCenterDot(dot, isInit) {
    this.pixelDeviation = ((dot / this.dotGrowth) % 5) * this.dotGrowth;
    gsap.to(this.bgContainer.position, { y: this.pixelDeviation, duration: isInit ? 0 : 0.7 });
    await gsap.to(this, { centerDot: dot, duration: isInit ? 0 : 0.7 });
  }

  async addDot(dot) {
    this.pointerDotDistance = 0;
    gsap.to(this, { pointerDotDistance: this.dotsMargin, ease: 'none', duration: 0.7 });
    gsap.to(this.bgContainer.position, {
      x: this.bgContainer.position.x - this.dotsMargin,
      ease: 'none',
      duration: 0.7,
    });
    await gsap.to(this, { pointerDot: dot, duration: 0.7, ease: 'none' });
    this.dots.push(dot);
    this.pointerDotDistance = 0;

    if (this.bgContainer.position.x <= -1 * 5 * this.dotsMargin) {
      this.bgContainer.position.x = 0;
    }
  }

  clearAllGsapAnimations() {
    gsap.globalTimeline.clear();
  }

  deleteUnnecessaryDots() {
    if (this.dots.length > deleteUnnecessaryDotsCount) {
      this.dots.shift();
      this.deleteUnnecessaryDots();
    }
  }

  dotToPoint(dot) {
    const { height } = pixiComponents.app.screen;
    return height / 2 - (dot - this.centerDot);
  }

  parseGraphicsPointsFromDots() {
    const canvasWidth = pixiComponents.app.screen.width;

    const b = [];
    b.push(this.dotToPoint(this.pointerDot), canvasWidth / 2);
    [...this.dots].reverse().forEach((dot, index) => {
      b.push(this.dotToPoint(dot));
      b.push(canvasWidth / 2 - this.pointerDotDistance - index * this.dotsMargin);
    });

    b.push(b[b.length - 2], b[b.length - 1]);

    this.graphicsPoints = b.reverse();
    this.synchronizeChartGraphics();

    this.deleteUnnecessaryDots();
  }

  createTextureChartFill() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const { width, height } = pixiComponents.app.renderer;

    canvas.width = width;
    canvas.height = height * 1.2;

    const gradient = ctx.createLinearGradient(0, 0, 0, height * 0.8);
    gradient.addColorStop(0, 'rgba(252, 213, 53, 0.7)');
    gradient.addColorStop(1, 'rgba(252, 213, 53, 0.05)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height * 1.2);

    this.texture = Texture.from(canvas);
  }

  addChartFillGraphics() {
    this.chartFillGraphics = new Graphics();
    this.synchronizeChartFillGraphics();
    this.container.addChild(this.chartFillGraphics);
  }

  synchronizeChartFillGraphics() {
    this.chartFillGraphics.clear();
    const { height, width } = pixiComponents.app.screen;
    this.chartFillGraphics.beginTextureFill({ texture: this.texture });
    this.chartFillGraphics.drawPolygon([
      ...this.graphicsPoints,
      width / 2,
      height * 1.2,
      0,
      height,
    ]);
    this.chartFillGraphics.endFill();
  }

  addChartStrokeGraphics() {
    this.chartStrokeGraphics = new Graphics();
    this.chartStrokeBlurGraphics = new Graphics();
    this.synchronizeChartStrokeGraphics();
    this.container.addChild(this.chartStrokeBlurGraphics, this.chartStrokeGraphics);
  }

  synchronizeChartStrokeGraphics() {
    this.chartStrokeGraphics.clear();
    this.chartStrokeBlurGraphics.clear();

    this.chartStrokeGraphics.lineStyle(3, 'rgba(252, 213, 53, 1)', 1);
    this.chartStrokeGraphics.moveTo(this.graphicsPoints[0], this.graphicsPoints[1]);
    this.chartStrokeBlurGraphics.lineStyle(3, 'rgba(252, 213, 53, 1)', 1);
    this.chartStrokeBlurGraphics.moveTo(this.graphicsPoints[0], this.graphicsPoints[1]);
    const blurFilter = new BlurFilter();
    blurFilter.blurY = 3;
    blurFilter.quality = 10;
    this.chartStrokeBlurGraphics.filters = [blurFilter];

    for (let i = 2; i < this.graphicsPoints.length; i += 2) {
      this.chartStrokeGraphics.lineTo(this.graphicsPoints[i], this.graphicsPoints[i + 1]);
      // this.chartStrokeBlurGraphics.lineTo(this.graphicsPoints[i], this.graphicsPoints[i + 1]);
    }
  }

  synchronizeChartGraphics() {
    this.synchronizeChartFillGraphics();
    this.synchronizeChartStrokeGraphics();
    this.updateChartPointer();
  }

  createChartPointerLineGraphics() {
    const { height, width } = pixiComponents.app.screen;
    this.chartPointerLineGraphics = new Graphics();
    this.chartPointerLineGraphics.lineStyle(1, 'rgba(252, 213, 53, 1)', 0.7);
    this.chartPointerLineGraphics.lineTo(0, 0);
    this.chartPointerLineGraphics.lineTo(0, height * 1.2);
    this.chartPointerLineGraphics.x = width / 2;
    this.container.addChild(this.chartPointerLineGraphics);
  }

  addChartPointerLine(dot) {
    this.ruler = new Container();
    const scalePointMiddleStep = isMobile() ? 111 : 171;
    const chartPoint = dot / this.dotGrowth;
    const centerScalePoint = chartPoint - (chartPoint % 20);
    const scalePointsArr = [
      centerScalePoint + 60,
      centerScalePoint + 40,
      centerScalePoint + 20,
      centerScalePoint,
      centerScalePoint - 20,
      centerScalePoint - 40,
      centerScalePoint - 60,
    ];
    const difference = ((chartPoint - centerScalePoint) * (isMobile() ? mobHeight : webHeight)) / 5;
    scalePointsArr.forEach((point, i) => {
      const y = scalePointMiddleStep;
      const linePoint = new Text(
        `${point}`,
        isMobile() ? chartLinePointerStylesMob : chartLinePointerStylesWeb
      );
      const line = new Graphics();
      line.lineStyle(1, 'rgba(252, 213, 53, 1)', 0.6);
      line.moveTo(0, 0);
      line.lineTo(5, 0);
      line.x = 1;
      line.y = i * y - scalePointMiddleStep + difference + (isMobile() ? 27 : 0);
      linePoint.x = isMobile() ? 8 : 10;
      linePoint.y = isMobile() ? -5 : -8;
      this.scalePoints.push({ line, linePoint });
      line.addChild(linePoint);
      this.ruler.addChild(line);
    });

    this.chartPointerLineGraphics.addChild(this.ruler);
  }

  moveChartPointers(curStep, lastStep) {
    this.scalePoints.forEach((item) => {
      const curStepValue = curStep / pixiComponents.PixiComponent.chart.dotGrowth;
      const lastStepValue = lastStep / pixiComponents.PixiComponent.chart.dotGrowth;
      const middleStep =
        Math.abs(curStepValue - (curStepValue % 5) - (lastStepValue - (lastStepValue % 5))) / 5;
      const yPosition = Math.abs(middleStep * (isMobile() ? mobHeight : webHeight));
      const currentDeviation =
        ((lastStep / pixiComponents.PixiComponent.chart.dotGrowth) % 5) *
        pixiComponents.PixiComponent.chart.dotGrowth;
      const y =
        (lastStep > curStep ? item.line.y + yPosition : item.line.y - yPosition) +
        currentDeviation -
        pixiComponents.PixiComponent.chart.pixelDeviation;
      gsap.to(item.line.position, {
        y,
        ease: 'none',
        duration: 0.7,
      });
    });
    setTimeout(() => {
      this.handleResetChartPoints();
      this.addChartPointerLine(lastStep);
    }, 700);
  }

  handleResetChartPoints() {
    this.chartPointerLineGraphics.removeChild(this.ruler);
    this.scalePoints = [];
  }

  addChartPointer() {
    this.chartPointerContainer = new Container();

    this.chartPointerBg = new Sprite(Texture.from(pointerBg));
    this.chartPointerBg.scale.set(0.5);

    this.chartPointerText = new Text('', isMobile() ? pointerStylesMob : pointerStylesWeb);

    this.chartPointerText.anchor.set(0.5, 0.5);
    this.chartPointerText.position.set(pointerX, pointerY);

    this.chartPointerContainer.addChild(this.chartPointerBg, this.chartPointerText);
    this.chartPointerContainer.pivot.set(pointerPivotX, pointerPivotY);

    this.container.addChild(this.chartPointerContainer);
  }

  updateChartPointer() {
    const { width } = pixiComponents.app.screen;
    this.chartPointerContainer.position.set(width / 2, this.dotToPoint(this.pointerDot));

    this.chartPointerText.text = (this.pointerDot / this.dotGrowth).toFixed(0); // fix 8y
  }

  addBackground() {
    const { width } = pixiComponents.app.screen;
    this.bgContainer = new Container();
    this.background1 = new Sprite(Texture.from(gameBg));
    this.background2 = new Sprite(Texture.from(gameBg));
    this.background1.scale.set(bgScale1);
    this.background2.scale.set(bgScale2);
    this.background1.anchor.set(bgAnchor1X, 0);
    this.background2.anchor.set(bgAnchor2X, 0);
    this.background1.x = width / 2;
    this.background2.x = width / 2;
    this.background1.y = bg1Y;
    this.background2.y = bg2Y;

    this.bgContainer.addChild(this.background1, this.background2);
    this.container.addChild(this.bgContainer);
  }

  handleResetData() {
    this.dots = [];
    this.pointerDot = 0;
    this.centerDot = 0;
  }
}
